import { Namespace, TFunction } from "i18next"
import { isValidPhoneNumber } from "libphonenumber-js/max"
import { ValidationRule } from "react-hook-form"

import { PhoneOrEmailFieldValue } from "@/globalComponents/organisms/PhoneOrEmailField"

export const requiredRule = (message: string): ValidationRule<boolean> => ({
  value: true,
  message: message,
})

export const validEmailRule = (message: string): ValidationRule<RegExp> => ({
  value: /^[\w.+-]+@[a-zA-Z\d.-]+\.[a-zA-Z]{2,}/,
  message,
})

export const passwordValidationRule = <Ns extends Namespace = Namespace>(
  t: TFunction<Ns>
) => ({
  minLength: {
    value: 8,
    message: t("common.password.minLength", { ns: "errors" }),
  },
  validate: (value: string) => {
    if (!/[A-Z]/.test(value)) {
      return t("common.password.uppercase", { ns: "errors" })
    }
    if (!/[a-z]/.test(value)) {
      return t("common.password.lowercase", { ns: "errors" })
    }
    if (!/\d/.test(value)) {
      return t("common.password.number", { ns: "errors" })
    }
    if (!/[@$!%*?&#]/.test(value)) {
      return t("common.password.special", { ns: "errors" })
    }
    if (!/^\S.*\S$/.test(value) && value.length > 1) {
      return t("common.password.noSpaces", { ns: "errors" })
    }
    return true
  },
})

export const licensePlateValidationRule =
  <Ns extends Namespace = Namespace>(t: TFunction<Ns>) =>
  (value: string) => {
    if (value.length < 2) {
      return t("common.licensePlate.minLength", { ns: "errors" })
    }
    if (value.length > 18) {
      return t("common.licensePlate.maxLength", { ns: "errors" })
    }
    if (!/^[A-Z0-9]+$/.test(value)) {
      return t("common.licensePlate.invalidCharacters", { ns: "errors" })
    }
    return true
  }

/**
 * Validation function for PhoneOrEmailFieldValue
 * Use with react-hook-form's validate rule
 */
export const phoneOrEmailValidationRule =
  <Ns extends Namespace = Namespace>(t: TFunction<Ns>) =>
  (value: PhoneOrEmailFieldValue) => {
    const selectedValue = value.value[value.mode]
    if (value.mode === "phone") {
      return phoneValidationRule(t)(selectedValue)
    } else {
      // Email mode - just check it's not empty (regex validation handled separately)
      if (!selectedValue || !selectedValue.trim()) {
        return t("common.validEmail", { ns: "errors" })
      }
    }
    return true
  }

/**
 * Validation function for standalone phone field
 * Use with react-hook-form's validate rule
 * Phone values are stored in E.164 format (e.g., "+12133734253")
 */
export const phoneValidationRule =
  <Ns extends Namespace = Namespace>(t: TFunction<Ns>) =>
  (value: string) => {
    if (!value || !isValidPhoneNumber(value)) {
      return t("common.validPhone", { ns: "errors" })
    }
    return true
  }
